import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Send, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loading } from "../../components/shared";

const TYPE_LABELS = {
  COMPLAINT: "Complaint",
  SERVICE_REQUEST: "Service request",
  MAINTENANCE_REQUEST: "Maintenance",
  COMMON_AREA_ISSUE: "Common area issue",
  SECURITY_ISSUE: "Security",
  OTHER: "Other",
};
const STATUS_TONE = {
  NEW: "bg-blue-100 text-blue-700",
  ACKNOWLEDGED: "bg-blue-100 text-blue-700",
  ASSIGNED: "bg-indigo-100 text-indigo-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  ON_HOLD: "bg-slate-200 text-slate-700",
  WAITING_FOR_RESIDENT: "bg-rose-100 text-rose-700",
  WAITING_FOR_VENDOR: "bg-slate-200 text-slate-700",
  RESOLVED: "bg-emerald-100 text-emerald-700",
  CLOSED: "bg-emerald-100 text-emerald-700",
  REOPENED: "bg-amber-100 text-amber-700",
  CANCELLED: "bg-slate-200 text-slate-500",
};
const niceStatus = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

export function StatusChip({ status }) {
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_TONE[status] || "bg-slate-200 text-slate-600"}`} data-testid={`ticket-status-${status}`}>{niceStatus(status)}</span>;
}

const SELECT_CLS = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none";
const STATUS_OPTIONS = ["NEW", "ACKNOWLEDGED", "ASSIGNED", "IN_PROGRESS", "ON_HOLD", "WAITING_FOR_RESIDENT", "WAITING_FOR_VENDOR", "RESOLVED", "CLOSED", "REOPENED", "CANCELLED"];

export default function ResidentHelpdesk() {
  const [boot, setBoot] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ q: "", status: "", category_id: "" });
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ ticket_type: "COMPLAINT", location_type: "UNIT", unit_id: "", category_id: "", subject: "", description: "", location: "" });

  const loadTickets = useCallback(async () => {
    const params = {};
    if (filters.q.trim()) params.q = filters.q.trim();
    if (filters.status) params.status = filters.status;
    if (filters.category_id) params.category_id = filters.category_id;
    const res = await api.get("/resident/helpdesk/tickets", { params });
    setTickets(res.data);
  }, [filters]);

  useEffect(() => {
    api.get("/resident/helpdesk/bootstrap")
      .then((b) => setBoot(b.data))
      .catch((e) => setError(e.response?.data?.detail || "Helpdesk is unavailable for this Society."));
  }, []);

  useEffect(() => {
    if (!boot) return;
    loadTickets().catch((e) => setError(e.response?.data?.detail || "Could not load your requests."));
  }, [boot, loadTickets]);

  const submit = async () => {
    if (!form.subject.trim()) return toast.error("Please add a subject");
    if (!form.category_id) return toast.error("Please choose a category");
    if (form.location_type === "UNIT" && !form.unit_id && boot.units.length > 1) return toast.error("Please choose a unit");
    setSubmitting(true);
    try {
      const payload = { ...form, idempotency_key: crypto.randomUUID() };
      if (form.location_type !== "UNIT") payload.unit_id = "";
      const res = await api.post("/resident/helpdesk/tickets", payload);
      if (file && res.data?.ticket_id) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/resident/helpdesk/tickets/${res.data.ticket_id}/attachments`, fd).catch(() => toast.error("Request saved but the attachment failed to upload"));
      }
      toast.success("Request submitted");
      setForm({ ticket_type: "COMPLAINT", location_type: "UNIT", unit_id: "", category_id: "", subject: "", description: "", location: "" });
      setFile(null);
      setShowForm(false);
      await loadTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) return <p className="border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600" data-testid="resident-helpdesk-error">{error}</p>;
  if (!boot || !tickets) return <Loading />;

  return (
    <div data-testid="resident-helpdesk-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Helpdesk</p>
          <h1 className="mt-2 font-display text-4xl font-bold">My requests</h1>
        </div>
        <Button data-testid="resident-helpdesk-new-toggle" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4" /> {showForm ? "Close" : "Raise a request"}
        </Button>
      </div>

      {showForm && (
        <section className="mt-6 border border-slate-200 bg-white p-5" data-testid="resident-helpdesk-create">
          <h2 className="font-display text-lg font-semibold">Raise a request</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block font-medium text-slate-600">Request type</span>
              <select className={SELECT_CLS} data-testid="resident-helpdesk-type" onChange={(e) => setForm({ ...form, ticket_type: e.target.value })} value={form.ticket_type}>
                {(boot.ticket_types || Object.keys(TYPE_LABELS)).map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-slate-600">Location</span>
              <select className={SELECT_CLS} data-testid="resident-helpdesk-location-type" onChange={(e) => setForm({ ...form, location_type: e.target.value })} value={form.location_type}>
                <option value="UNIT">My unit</option>
                <option value="COMMON_AREA">Common area</option>
              </select>
            </label>
            {form.location_type === "UNIT" && (
              <label className="text-sm">
                <span className="mb-1 block font-medium text-slate-600">Unit</span>
                <select className={SELECT_CLS} data-testid="resident-helpdesk-unit" onChange={(e) => setForm({ ...form, unit_id: e.target.value })} value={form.unit_id}>
                  <option value="">{boot.units.length > 1 ? "Choose a unit" : "Current unit"}</option>
                  {boot.units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_number}</option>)}
                </select>
              </label>
            )}
            {form.location_type === "COMMON_AREA" && (
              <label className="text-sm">
                <span className="mb-1 block font-medium text-slate-600">Area detail</span>
                <Input data-testid="resident-helpdesk-location" onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Lobby, Lift B, Parking" value={form.location} />
              </label>
            )}
            <label className="text-sm">
              <span className="mb-1 block font-medium text-slate-600">Category</span>
              <select className={SELECT_CLS} data-testid="resident-helpdesk-category" onChange={(e) => setForm({ ...form, category_id: e.target.value })} value={form.category_id}>
                <option value="">Choose category</option>
                {boot.categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="mb-1 block font-medium text-slate-600">Subject</span>
              <Input data-testid="resident-helpdesk-subject" onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Short summary" value={form.subject} />
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="mb-1 block font-medium text-slate-600">Description</span>
              <Textarea data-testid="resident-helpdesk-description" onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the issue" value={form.description} />
            </label>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <Paperclip className="h-4 w-4 text-slate-500" />
              <input accept="image/jpeg,image/png,image/webp,application/pdf" data-testid="resident-helpdesk-attachment" onChange={(e) => setFile(e.target.files?.[0] || null)} type="file" />
            </label>
          </div>
          <Button className="mt-4" data-testid="resident-helpdesk-submit" disabled={submitting} onClick={submit}>
            <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit request"}
          </Button>
        </section>
      )}

      <section className="mt-7 border border-slate-200 bg-white p-4" data-testid="resident-helpdesk-filters">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" data-testid="resident-helpdesk-search" onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search ticket # or subject" value={filters.q} />
          </div>
          <select className={SELECT_CLS} data-testid="resident-helpdesk-filter-status" onChange={(e) => setFilters({ ...filters, status: e.target.value })} value={filters.status}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{niceStatus(s)}</option>)}
          </select>
          <select className={SELECT_CLS} data-testid="resident-helpdesk-filter-category" onChange={(e) => setFilters({ ...filters, category_id: e.target.value })} value={filters.category_id}>
            <option value="">All categories</option>
            {boot.categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
        </div>
      </section>

      <section className="mt-6" data-testid="resident-helpdesk-list">
        <div className="space-y-3">
          {tickets.length ? tickets.map((t) => (
            <Link className="block border border-slate-200 bg-white p-4 transition-colors hover:border-primary" data-testid={`resident-ticket-${t.ticket_id}`} key={t.ticket_id} to={`/resident/helpdesk/${t.ticket_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium">{t.ticket_number} · {t.subject}</p>
                <div className="flex items-center gap-2">
                  {t.status === "WAITING_FOR_RESIDENT" && <span className="inline-flex rounded-full bg-rose-600 px-2.5 py-0.5 text-xs font-semibold text-white" data-testid={`resident-ticket-action-${t.ticket_id}`}>Action required</span>}
                  <StatusChip status={t.status} />
                </div>
              </div>
              <p className="mt-1 text-sm text-slate-600">{t.category_name} · {TYPE_LABELS[t.ticket_type] || t.ticket_type} · {t.location_type === "UNIT" ? (t.unit_number || "Unit") : "Common area"}</p>
            </Link>
          )) : <p className="border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600" data-testid="resident-helpdesk-empty">You have no service requests yet. Tap “Raise a request” to create one.</p>}
        </div>
      </section>
    </div>
  );
}
